import { useCallback, useState } from 'react';

interface UseImageAspectRatioProps {
  offsetPercentage?: number;
}

interface UseImageAspectRatioReturn {
  aspectRatio: string;
  isLoaded: boolean;
  imgRef: (img: HTMLImageElement | null) => void;
  onLoad: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

const useImageAspectRatio = ({
  offsetPercentage = 0,
}: UseImageAspectRatioProps = {}): UseImageAspectRatioReturn => {
  const [aspectRatio, setAspectRatio] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const handleImageLoaded = useCallback(
    (img: HTMLImageElement) => {
      const { naturalWidth, naturalHeight } = img;

      if (naturalWidth && naturalHeight) {
        const heightOffset = Math.ceil(naturalHeight * offsetPercentage);
        setAspectRatio(`${naturalWidth}/${naturalHeight - heightOffset}`);
      }
      setIsLoaded(true);
    },
    [offsetPercentage]
  );

  // Ref callback to handle cached images that load before onLoad is attached
  const imgRef = useCallback(
    (img: HTMLImageElement | null) => {
      if (img && img.complete && img.naturalWidth > 0 && !isLoaded) {
        handleImageLoaded(img);
      }
    },
    [handleImageLoaded, isLoaded]
  );

  const onLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      if (!isLoaded) {
        handleImageLoaded(event.currentTarget);
      }
    },
    [handleImageLoaded, isLoaded]
  );

  return { aspectRatio, isLoaded, imgRef, onLoad };
};

export default useImageAspectRatio;
